// src/components/CampaignWizard/CampaignWizardModal.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Row,
    Col,
    Card,
    CardBody,
    Button
} from "reactstrap";
import { withTranslation, WithTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ApplicationState } from "store";
import {
    campaignCreateRequest,
    campaignGetRequest,
    campaignUpdateRequest,
    campaignScheduleRequest,
} from "store/campaigns/actions";

import StepBasicInfo from "./StepBasicInfo";
import StepChannels, { ChannelType } from "./StepChannels";
import StepAudience from "./StepAudience";
import StepSchedule from "./StepSchedule";
import StepFallback, { FallbackRule } from "./StepFallback";
import StepCost from "./StepCost";
import StepPreview from "./StepPreview";
import StepReview from "./StepReview";
import SummarySidebar, { SummaryItem } from "./SummarySidebar";

type BasicInfo = {
    name: string;
    description: string;
    campaignType: "MARKETING" | "TRANSACTIONAL";
};

type ChannelState = {
    channels: Record<ChannelType, boolean>;
    templateByChannel: Partial<Record<ChannelType, string>>;
};

type Audience = {
    type: "STATIC" | "DYNAMIC";
    csvFile?: File | null;
    filterGroups: any[];
};

type Schedule = {
    immediate: boolean;
    scheduledTime: Date | null;
    localTime: boolean;
    recurrence: any | null;
};

type CostEstimate = {
    emailCost: number;
    smsCost: number;
    inAppCost: number;
    totalCost: number;
};

type PreviewData = {
    emailHtml: string;
    smsText: string;
    inApp: { title: string; body: string };
};

const STEPS = [
    { icon: "ri-edit-box-line", labelKey: "Basic Info" },
    { icon: "ri-mail-send-line", labelKey: "Channels & Templates" },
    { icon: "ri-group-line", labelKey: "Audience" },
    { icon: "ri-calendar-line", labelKey: "Schedule" },
    { icon: "ri-repeat-line", labelKey: "Fallback Rules" },
    { icon: "ri-price-tag-3-line", labelKey: "Cost Estimate" },
    { icon: "ri-eye-line", labelKey: "Preview" },
    { icon: "ri-check-double-line", labelKey: "Review & Submit" },
] as const;

const CampaignWizardModal: React.FC<WithTranslation> = ({ t }) => {
    const dispatch = useDispatch<any>();
    const navigate = useNavigate();
    const { id } = useParams<{ id?: string }>();
    const existing = useSelector((s: ApplicationState) => s.campaign.current);

    const [isOpen, setIsOpen] = useState(true);
    const close = () => {
        setIsOpen(false);
        navigate("/campaigns");
    };

    const [step, setStep] = useState(0);

    // ── wizard state ────────────────────────────
    const [basicInfo, setBasicInfo] = useState<BasicInfo>({
        name: "",
        description: "",
        campaignType: "MARKETING",
    });

    const [channelState, setChannelState] = useState<ChannelState>({
        channels: { EMAIL: false, SMS: false, IN_APP: false },
        templateByChannel: {},
    });

    const [audience, setAudience] = useState<Audience>({
        type: "STATIC",
        csvFile: null,
        filterGroups: [],
    });


    const [schedule, setSchedule] = useState<Schedule>({
        immediate: true,
        scheduledTime: null,
        localTime: false,
        recurrence: null,
    });

    const [fallbackRules, setFallbackRules] = useState<FallbackRule[]>([]);
    const [cost, setCost] = useState<CostEstimate>({
        emailCost: 0,
        smsCost: 0,
        inAppCost: 0,
        totalCost: 0,
    });

    const [preview, setPreview] = useState<PreviewData>({
        emailHtml: "",
        smsText: "",
        inApp: { title: "", body: "" },
    });

    // ── load existing campaign on edit ──────────
    useEffect(() => {
        if (id) dispatch(campaignGetRequest(id));
    }, [id, dispatch]);

    useEffect(() => {
        if (!existing) return;

        setBasicInfo({
            name: existing.name,
            description: existing.description || "",
            campaignType: existing.campaign_type as any,
        });

        setChannelState({
            channels: {
                EMAIL: existing.channels.includes("EMAIL"),
                SMS: existing.channels.includes("SMS"),
                IN_APP: existing.channels.includes("IN_APP"),
            },
            templateByChannel: {
                EMAIL: (existing as any).emailTemplateId,
                SMS: (existing as any).smsTemplateId,
                IN_APP: (existing as any).inAppTemplateId,
            },
        });

        // TODO: hydrate `audience`, `schedule`, `fallbackRules`
    }, [existing]);

    // ── per-step validation ─────────────────────
    const canNext = () => {
        switch (step) {
            case 0:
                return !!basicInfo.name.trim();
            case 1:
                // return Object.entries(channelState.channels).some(
                //   ([ch, on]) => on && !!channelState.templateByChannel[ch as ChannelType]
                // );
                return true
            case 2:
                // return audience.type === "STATIC"
                //     ? !!audience.csvFile
                //     : !!(audience as any).filterJson;

                return true
            case 3:
                return schedule.immediate || !!schedule.scheduledTime;
            default:
                return true;
        }
    };
    const onNext = () => {
        if (step < STEPS.length - 1 && canNext()) setStep((s) => s + 1);
    };
    const onBack = () => {
        if (step > 0) setStep((s) => s - 1);
    };

    // ── final submit ────────────────────────────
    const onFinish = async () => {
        const selectedChannels = (Object.keys(channelState.channels) as ChannelType[])
            .filter((ch) => channelState.channels[ch]);

        const payload = {
            name: basicInfo.name,
            description: basicInfo.description,
            campaignType: basicInfo.campaignType,
            channels: selectedChannels,
            templates: channelState.templateByChannel,
            // TODO: add `audience`, `fallbackRules`, etc.
        };

        let newId = id;
        if (!id) {
            const res: any = await dispatch(campaignCreateRequest(payload));
            newId = res.payload.id;
        } else {
            await dispatch(campaignUpdateRequest(id!, payload));
        }

        await dispatch(
            campaignScheduleRequest(newId!, {
                immediate: schedule.immediate,
                scheduledTime: schedule.scheduledTime?.toISOString(),
                localTime: schedule.localTime,
                recurrence: schedule.recurrence,
            })
        );

        close();
    };

    // ── stepper UI ─────────────────────────────
    const renderStepper = () => (
        <div className="px-4 py-3">
            <div className="d-flex justify-content-between align-items-start gap-4">
                {STEPS.map((s, i) => {
                    const completed = i < step;
                    const active = i === step;
                    const circleClass = completed
                        ? "bg-success text-white"
                        : active
                            ? "bg-primary text-white"
                            : "bg-white border border-secondary text-secondary";

                    return (
                        <div
                            key={i}
                            className="text-center flex-fill d-flex flex-column align-items-center"
                            style={{ cursor: completed ? "pointer" : undefined }}
                            onClick={() => completed && setStep(i)}
                        >
                            <div
                                className={`rounded-circle shadow-sm d-flex align-items-center justify-content-center ${circleClass}`}
                                style={{ width: 48, height: 48, fontSize: 20, transition: "all .2s" }}
                            >
                                {completed ? <i className="ri-check-line" /> : <i className={s.icon} />}
                            </div>
                            <div
                                className={`mt-2 small ${completed ? "text-success" : active ? "text-primary fw-semibold" : "text-muted"
                                    }`}
                                style={{ fontSize: "0.85rem" }}
                            >
                                {t(s.labelKey)}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    // ── summary sidebar ─────────────────────────
    const summary: SummaryItem[] = useMemo(() => {
        const channelsList = (Object.entries(channelState.channels) as [ChannelType, boolean][])
            .filter(([_, on]) => on)
            .map(([ch]) => t(ch.toLowerCase()));
        return [
            {
                icon: "ri-edit-line",
                title: t("Basic Info"),
                detail: basicInfo.name || t("Not set"),
            },
            {
                icon: "ri-mail-send-line",
                title: t("Channels"),
                detail: channelsList.length > 0 ? channelsList.join(", ") : t("None"),
            },
            {
                icon: "ri-group-line",
                title: t("Audience"),
                detail: audience.type === "STATIC" ? t("CSV") : t("Filter"),
                badgeText: audience.type === "STATIC" && audience.csvFile ? audience.csvFile.name : undefined,
                badgeColor: audience.type === "STATIC" ? "info" : "secondary",
            },
            {
                icon: "ri-calendar-line",
                title: t("Schedule"),
                detail: schedule.immediate
                    ? t("Immediate")
                    : schedule.scheduledTime
                        ? `${schedule.scheduledTime.toLocaleString()}${schedule.localTime ? ` (${t("Local")})` : ""}`
                        : t("None"),
            },
            {
                icon: "ri-repeat-line",
                title: t("Fallback Rules"),
                detail: fallbackRules.length ? `${fallbackRules.length} ${t("rule", { count: fallbackRules.length })}` : t("None"),
            },
            {
                icon: "ri-price-tag-3-line",
                title: t("Cost Estimate"),
                detail: `$${cost.totalCost.toFixed(2)}`,
            },
        ];
    }, [basicInfo, channelState, audience, schedule, fallbackRules, cost, t]);

    return (
        <Modal isOpen={isOpen} fullscreen toggle={close} className="modal-premium">
            <ModalHeader
                toggle={close}
                className="bg-white border-bottom px-4 py-3 shadow-sm d-flex align-items-center"
                style={{ fontSize: "1.1rem", fontWeight: 600 }}
            >
                {id ? t("Edit Campaign") : t("New Campaign")}
            </ModalHeader>

            <ModalBody className="px-4 py-3 bg-light">
                {renderStepper()}

                <Row className="gx-4">
                    <Col lg={8}>
                        <Card className="shadow-sm border-light-subtle rounded-4 p-4 bg-white">
                            <CardBody className="p-0">
                                {step === 0 && (
                                    <StepBasicInfo data={basicInfo} onChange={setBasicInfo} onNext={onNext} />
                                )}
                                {step === 1 && (
                                    <StepChannels
                                        data={channelState}
                                        onChange={(patch) =>
                                            setChannelState((prev) => ({
                                                channels: patch.channels ?? prev.channels,
                                                templateByChannel:
                                                    patch.templateByChannel ?? prev.templateByChannel,
                                            }))
                                        }
                                        onNext={onNext}
                                        onBack={onBack}
                                    />
                                )}
                                {step === 2 && (
                                    <StepAudience
                                        data={audience}
                                        dataFields={[]}           // ← whatever fields your Filter expects
                                        onChange={(patch) =>
                                            setAudience((prev) => ({ ...prev, ...patch }))
                                        }
                                        onNext={onNext}
                                        onBack={onBack}
                                        t={t}
                                    />
                                )}
                                {step === 3 && (
                                    <StepSchedule
                                        data={schedule}
                                        onChange={setSchedule}
                                        onNext={onNext}
                                        onBack={onBack}
                                    />
                                )}
                                {step === 4 && (
                                    <StepFallback
                                        data={fallbackRules}
                                        onChange={setFallbackRules}
                                        onNext={onNext}
                                        onBack={onBack}
                                    />
                                )}
                                {step === 5 && (
                                    <StepCost data={cost} setData={setCost} onNext={onNext} onBack={onBack} />
                                )}
                                {step === 6 && (
                                    <StepPreview
                                        data={preview}
                                        enabledChannels={(
                                            Object.keys(channelState.channels) as ChannelType[]
                                        ).filter((ch) => channelState.channels[ch])}
                                        setData={setPreview}
                                        onNext={onNext}
                                        onBack={onBack}
                                    />
                                )}
                                {step === 7 && (
                                    <StepReview
                                        basicInfo={basicInfo}
                                        channels={channelState.channels}
                                        audience={audience}
                                        schedule={schedule}
                                        fallbackRules={fallbackRules}
                                        costEstimate={cost}
                                        previewData={preview}
                                        onBack={onBack}
                                        onSubmit={onFinish}
                                    />
                                )}
                            </CardBody>
                        </Card>
                    </Col>

                    <Col lg={4}>
                        <SummarySidebar items={summary} topOffset={80} />
                    </Col>
                </Row>
            </ModalBody>

            <ModalFooter className="bg-white border-top shadow-sm px-4 py-3 d-flex justify-content-between">
                <Button color="outline-secondary" disabled={step === 0} onClick={onBack}>
                    <i className="ri-arrow-left-line me-1" /> {t("Back")}
                </Button>

                {step < STEPS.length - 1 ? (
                    <Button color="primary" disabled={!canNext()} onClick={onNext}>
                        {t("Next")} <i className="ri-arrow-right-line ms-1" />
                    </Button>
                ) : (
                    <Button color="success" onClick={onFinish}>
                        {id ? t("Update & Schedule") : t("Create & Schedule")}
                    </Button>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default withTranslation()(CampaignWizardModal);
